import { useState, useRef, useEffect, useCallback, Suspense } from 'react';
import { assistantApi } from '../api/assistant';
import { agentCallsApi, type AgentCallRequest } from '../api/agentCalls';
import { ringsApi } from '../api/rings';
import { chatApi } from '../api/chat';
import { voiceApi } from '../api/voice';
// Voice Mode 2.0 — P1 live loop (feature-flagged server-side; the button only
// appears when GET /voice/config reports voice_live).
import { useVoiceLive, microphoneAlreadyGranted } from '../lib/voiceLive/useVoiceLive';
import { sanitizeForSpeech } from '../lib/speechText';
import { useWorkspace } from '../hooks/useWorkspace';
import { useAvatarStateMachine } from '../hooks/useAvatarStateMachine';
import { notifyProcessing, notifySpeakingStart, notifySpeakingStop, notifyError, notifyNewMessage, notifyUserActivity, notifySuccess } from '../lib/avatarEvents';
import { Check, Copy, Mic, Radio, Volume2, VolumeX, MessageSquare } from 'lucide-react';
import AvatarAnimatorLazy from './AvatarAnimatorLazy';

interface WidgetMessage {
  role: 'user' | 'assistant';
  content: string;
  /** Server message id, when known — used to track what the user has read. */
  id?: string;
}

export default function AssistantWidget({
  assistantName,
  personalityMode,
  avatarId,
  customAvatarPath,
  soundEnabled,
  animationEnabled,
  bubbleModeEnabled,
  avatarIdRicardo,
  avatarIdHenry,
}: {
  assistantName: string;
  personalityMode: string;
  avatarId: string;
  customAvatarPath: string | null;
  soundEnabled: boolean;
  animationEnabled: boolean;
  bubbleModeEnabled?: boolean;
  avatarIdRicardo?: string | null;
  avatarIdHenry?: string | null;
}) {
  const { currentWorkspace } = useWorkspace();
  const { avatarState } = useAvatarStateMachine();
  // ── Agent selection (owner directive 2026-10-09) ──────────────────────
  // The widget may talk to the default assistant, Ricardo (this server's
  // OpenClaw) or Henry (the Mac OpenClaw). Choosing one switches the visible
  // identity AND routes the chat + live voice to that agent's REAL OpenClaw
  // conversation. Persisted so it survives reloads.
  type AgentLine = 'default' | 'ricardo' | 'henry';
  const AGENTS: Record<AgentLine, { name: string; emoji: string; avatar: string; label: string }> = {
    default: { name: assistantName, emoji: '💬', avatar: avatarId, label: 'Assistant' },
    ricardo: { name: 'Ricardo', emoji: '🎩', avatar: 'crown_butler', label: 'Ricardo' },
    henry: { name: 'Henry', emoji: '😇', avatar: 'saik_assistant', label: 'Henry' },
  };
  const [agentLine, setAgentLine] = useState<AgentLine>(() => {
    try {
      const v = localStorage.getItem('saik_widget_agent');
      return v === 'ricardo' || v === 'henry' ? v : 'default';
    } catch { return 'default'; }
  });
  const agent = AGENTS[agentLine];
  const effName = agent.name;
  // Per-agent avatar (owner directive 2026-10-10): each of the three lines can
  // have its own avatar; NULL falls back to the line default / main avatar.
  const perAgentAvatarId = agentLine === 'ricardo' ? (avatarIdRicardo || null)
    : agentLine === 'henry' ? (avatarIdHenry || null) : null;
  const effAvatar = perAgentAvatarId || (agentLine === 'default' ? avatarId : agent.avatar);
  // Voice interaction mode (owner directive 2026-10-10): a PARALLEL set of modes
  // that all use the mic, so ONLY ONE runs at a time. "normal" = neither (tap the
  // mic to dictate / the hands-free button for voice); "voice" = hands-free live
  // voice; "bubble" = speech bubble (short spoken replies beside the avatar).
  // Switched from the widget long-press menu. PERSISTED (owner bug report
  // 2026-10-13 02:29, #1/#2): the chosen mode survives closing the app —
  // restored on load (see the restore effect below); the system setting
  // bubble_mode_enabled seeds it to "bubble" when nothing was saved yet.
  const [voiceMode, setVoiceMode] = useState<'normal' | 'voice' | 'bubble'>(() => {
    try {
      const saved = localStorage.getItem('saik_widget_voicemode');
      if (saved === 'voice' || saved === 'bubble' || saved === 'normal') return saved;
    } catch { /* ignore */ }
    return bubbleModeEnabled ? 'bubble' : 'normal';
  });
  // NOTE (owner bug report 2026-10-13 14:26): do NOT re-force "bubble" after
  // mount. The initializer above already seeds "bubble" only when NOTHING was
  // saved; an effect that flips "normal" back to "bubble" un-turns-off the
  // feature every load, which is exactly the reported bug.
  const bubbleMode = voiceMode === 'bubble';
  // Captured during the FIRST render: the persisted mode this load started
  // from. Only THAT value may trigger the load-time mic restore — a mode
  // enabled later by a user action (ANSWER, hands-free tap) starts its own mic
  // inside the gesture and must never re-enter the restore path.
  const mountedVoiceModeRef = useRef<'normal' | 'voice' | 'bubble' | undefined>(undefined);
  if (mountedVoiceModeRef.current === undefined) mountedVoiceModeRef.current = voiceMode;
  // The OpenClaw conversation the widget is currently bound to (Ricardo/Henry).
  const boundConvRef = useRef<string | null>(null);
  const [agentBusy, setAgentBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [messages, setMessages] = useState<WidgetMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(-1);
  const [bubble, setBubble] = useState<{ title: string; body: string } | null>(null);
  const [position, setPosition] = useState(() => {
    // Restore where the user last put the avatar (owner 2026-10-10: it kept
    // drifting low). Clamped to the viewport on read, so a stale/rotated size
    // can never place it off-screen.
    try {
      const raw = localStorage.getItem('saik_widget_pos');
      if (raw) {
        const p = JSON.parse(raw) as { x?: number; y?: number };
        if (typeof p?.x === 'number' && typeof p?.y === 'number') {
          return clampPosition(p.x, p.y);
        }
      }
    } catch { /* ignore */ }
    const touch = window.matchMedia('(pointer: coarse)').matches || window.innerWidth < 640;
    return touch
      ? { x: window.innerWidth - 90, y: window.innerHeight - 140 }
      : { x: window.innerWidth - 110, y: window.innerHeight - 160 };
  });
  const [dragging, setDragging] = useState(false);
  const [isTouchLayout, setIsTouchLayout] = useState(() =>
    window.matchMedia('(pointer: coarse)').matches || window.innerWidth < 640
  );
  const dragRef = useRef<{ offsetX: number; offsetY: number }>({ offsetX: 0, offsetY: 0 });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const bubbleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const greetedRef = useRef(false);
  // ── An ANSWERED call IS the introduction (owner report 2026-10-13 01:26) ────
  // He answers an agent call; the agent's first response IS the call content
  // (correct). But a SECOND auto-generated introduction used to appear once the
  // chat (re-)opened — "the old introductions into the open chat". Fix rule: for a
  // reasonable window after ANY answered call, opening the chat NEVER triggers a
  // fresh auto-greet — the answered call was the introduction for that visit.
  const ANSWERED_CALL_GREET_SUPPRESS_MS = 30 * 60 * 1000; // the whole visit
  const answeredCallAtRef = useRef(0);
  const answeredCallSuppressesGreet = () =>
    Date.now() - answeredCallAtRef.current < ANSWERED_CALL_GREET_SUPPRESS_MS;
  // ── Ring + auto-speak on unread open (owner directive 2026-10-12) ──────
  // A small mute toggle (header speaker icon) controls BOTH the ring and the
  // auto-speak; persisted so it survives reloads; default ON (sound enabled).
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem('saik_widget_mute') === '1'; } catch { return false; }
  });
  const speakTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const speakCancelledRef = useRef(false);
  const ringCheckedRef = useRef(false);
  // Fresh-value mirrors for the single auto-speak gate. The long-lived
  // 'saik:voice-turn' listener is registered once, so reading muted/live directly
  // would capture stale values; the gate reads these refs instead.
  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  const liveActiveRef = useRef(false);
  // Suppresses the ring for the ONLY two cases that must stay silent:
  //  (1) the user's own sent turn (their reply is read, not an incoming message),
  //  (2) an active live-voice session (the live loop owns speech).
  // App-launch AUTO-OPEN is deliberately NOT suppressed — an unread incoming
  // message must ring + auto-speak on launch (owner directive 2026-10-12).
  const suppressRingRef = useRef(false);

  const avatarColors: Record<string, string> = {
    crown_butler: 'from-yellow-400 to-amber-600',
    classic_robot: 'from-blue-400 to-blue-600',
    friendly_ghost: 'from-gray-200 to-gray-400',
    cosmic_orb: 'from-purple-400 to-purple-700',
    sharp_robot: 'from-red-400 to-red-600',
    pixel_heart: 'from-pink-400 to-pink-600',
    custom: 'from-indigo-400 to-indigo-600',
  };

  const gradient = avatarColors[effAvatar] || avatarColors.crown_butler;

  // Resolve avatar image URL — all bundled avatars now use PNG images
  const BUNDLED_AVATAR_IMAGES: Record<string, string> = {
    crown_butler: '/avatars/crown_butler.png',
    classic_robot: '/avatars/classic_robot.png',
    friendly_ghost: '/avatars/friendly_ghost.png',
    cosmic_orb: '/avatars/cosmic_orb.png',
    sharp_robot: '/avatars/sharp_robot.png',
    pixel_heart: '/avatars/pixel_heart.png',
    saik_assistant: '/avatars/saik_assistant.png',
  };
  const avatarImagePath = perAgentAvatarId
    ? (BUNDLED_AVATAR_IMAGES[perAgentAvatarId] || null)
    : (customAvatarPath || BUNDLED_AVATAR_IMAGES[effAvatar] || null);
  const isFullBodyAvatar = !!avatarImagePath;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Keep the avatar strictly INSIDE the viewport. Clamped on every position
  // change and on resize/rotation, so it can never drift to a low or off-screen
  // corner (owner report 2026-10-10: "the widget is moving so much lower now").
  function clampPosition(x: number, y: number) {
    const touch = window.matchMedia('(pointer: coarse)').matches || window.innerWidth < 640;
    const w = touch ? 80 : 90;
    const h = touch ? 120 : 135;
    const maxX = Math.max(10, window.innerWidth - w - 10);
    const maxY = Math.max(10, window.innerHeight - h - 20);
    return { x: Math.max(10, Math.min(x, maxX)), y: Math.max(10, Math.min(y, maxY)) };
  }
  const persistPosition = (p: { x: number; y: number }) => {
    try { localStorage.setItem('saik_widget_pos', JSON.stringify(p)); } catch { /* ignore */ }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const syncLayout = () => {
      const touchLayout = window.matchMedia('(pointer: coarse)').matches || window.innerWidth < 640;
      setIsTouchLayout(touchLayout);
      // Keep the (dragged) avatar inside the viewport: an off-screen position
      // made the whole page pan sideways on phones, and a stale value could
      // leave it stuck low. Clamp on every layout change; never re-anchor it.
      setPosition(prev => {
        const next = clampPosition(prev.x, prev.y);
        persistPosition(next);
        return next;
      });
    };
    syncLayout();
    window.addEventListener('resize', syncLayout);
    window.addEventListener('orientationchange', syncLayout);
    return () => {
      window.removeEventListener('resize', syncLayout);
      window.removeEventListener('orientationchange', syncLayout);
    };
  }, []);

  const [voiceActive, setVoiceActive] = useState(false);
  const [liveVoiceEnabled, setLiveVoiceEnabled] = useState(false);

  // Live voice (P1): only offered when the server reports the flag is on.
  useEffect(() => {
    let alive = true;
    voiceApi
      .config()
      .then((config) => {
        if (alive) setLiveVoiceEnabled(Boolean(config.voice_live));
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  // The conversation the widget is bound to (the selected agent's real OpenClaw
  // line, or the daily session). Live voice binds to it so it talks to the same
  // agent the widget shows.
  const [convId, setConvId] = useState<string | null>(null);
  const resolveAgentConversation = useCallback(async (line: AgentLine, wsId: string): Promise<string> => {
    if (line === 'ricardo') return (await chatApi.openclawConversation(wsId)).id;
    if (line === 'henry') return (await chatApi.macConversation(wsId)).id;
    return (await chatApi.dailyReset(wsId)).id;
  }, []);

  // surface:"widget" (or "widget_bubble") — the floating widget is allowed to
  // drive on-screen navigation (the chat page is not).
  const live = useVoiceLive({
    workspaceId: currentWorkspace?.id ?? null,
    surface: bubbleMode ? 'widget_bubble' : 'widget',
    conversationId: convId,
  });
  // Kept fresh for the auto-speak gate below (live owns speech while active).
  liveActiveRef.current = live.active;

  // Resolve + bind the selected agent's REAL conversation and load its history.
  useEffect(() => {
    const wsId = currentWorkspace?.id;
    if (!wsId) return;
    let cancelled = false;
    resolveAgentConversation(agentLine, wsId)
      .then((id) => {
        if (cancelled) return null;
        boundConvRef.current = id;
        setConvId(id);
        return chatApi.messages(id);
      })
      .then((rows) => {
        if (cancelled || !rows) return;
        const mapped = rows
          .filter((m) => m.role !== 'system' && (m.content || '').trim())
          .map((m): WidgetMessage => ({ role: m.role === 'user' ? 'user' : 'assistant', content: m.content, id: m.id }));
        // Answering Kimmy's call while another line was selected: her debrief
        // lands ON TOP of her history in one update — no race, no wipe
        // (owner correction 2026-10-12 23:12). The text rides a REF (the
        // state is already cleared at the answer tap).
        let greetText: string | null = null;
        if (injectGreetAfterLoadRef.current && agentLine === 'default' && injectGreetTextRef.current) {
          greetText = injectGreetTextRef.current;
          injectGreetAfterLoadRef.current = false;
          injectGreetTextRef.current = null;
          setPendingGreet(null);
        }
        setMessages(greetText ? [...mapped, { role: 'assistant', content: greetText }] : mapped);
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [agentLine, currentWorkspace?.id, resolveAgentConversation]);

  const switchAgent = (line: AgentLine) => {
    try { localStorage.setItem('saik_widget_agent', line); } catch { /* ignore */ }
    setAgentLine(line);
    setMessages([]);
    greetedRef.current = false;
    ringCheckedRef.current = false; // a fresh line re-evaluates its unread state
  };

  // Persistence (owner bug report 2026-10-13 02:29, #2): the voice mode and
  // the live-voice preference survive an app close/re-open, exactly like the
  // agent selection / position / mute settings already do.
  const persistVoiceMode = (m: 'normal' | 'voice' | 'bubble') => {
    try { localStorage.setItem('saik_widget_voicemode', m); } catch { /* ignore */ }
  };
  const persistLiveVoicePref = (on: boolean) => {
    try { localStorage.setItem('saik_widget_live_voice', on ? '1' : '0'); } catch { /* ignore */ }
  };
  // Switch the voice interaction mode (owner directive 2026-10-10). Tapping the
  // active mode again returns to "normal" (mic off). Only one of voice/bubble may
  // run at once — they share the mic.
  const changeVoiceMode = (next: 'normal' | 'voice' | 'bubble') => {
    setVoiceMode(next);
    persistVoiceMode(next);
    persistLiveVoicePref(next !== 'normal');
    setShowContextMenu(false);
    if (next === 'bubble') setExpanded(false); // the bubble is the surface; close any panel
  };

  // ── ENABLE voice mode from a tap (owner bug report 2026-10-13 02:29) ─────
  // One helper for every "this tap must end in a working voice mode" control:
  //  • the hands-free mic button when voice mode is off (#4 — never a dead mic
  //    that transcribes into a silent void), and
  //  • the ANSWER button on a call (#3 — an answered call IS a voice call).
  // It persists the choice (#2), flips the mode/state visibly, and starts the
  // live mic INSIDE the gesture (iOS only unlocks the AudioContext from a real
  // user tap). In bubble mode the surface must switch to "widget", which the
  // mode-change effect below handles by restarting the session.
  const enableVoiceMode = () => {
    persistVoiceMode('voice');
    persistLiveVoicePref(true);
    setLiveVoiceEnabled(true);
    setVoiceMode('voice');
    // Marked so the mode-change effect never re-dispatches behind this gesture
    // (a second start-live would re-open a chat the user just closed and fire
    // redundant state updates).
    liveRequestedAtRef.current = Date.now();
    if (!bubbleMode) {
      try { window.dispatchEvent(new CustomEvent('saik:start-live')); } catch { /* ignore */ }
    }
  };

  // Drive the live mic when the mode changes (owner directive 2026-10-10):
  //  - to "normal": stop the session;
  //  - to "voice"/"bubble": start the live loop with that surface (restart if the
  //    surface changed, e.g. switching voice <-> bubble, which is fixed per session).
  // Only fires on a real transition, so a manual stop is never auto-restarted.
  const prevModeRef = useRef<'normal' | 'voice' | 'bubble'>(voiceMode);
  // Timestamp of the last in-gesture live start (ANSWER / hands-free tap) —
  // the mode effect must not shadow it with a redundant delayed dispatch.
  const liveRequestedAtRef = useRef(0);
  useEffect(() => {
    const prev = prevModeRef.current;
    prevModeRef.current = voiceMode;
    if (voiceMode === prev) return;
    const startLive = () => { try { window.dispatchEvent(new CustomEvent('saik:start-live')); } catch { /* ignore */ } };
    if (voiceMode === 'normal') {
      if (live.active) endLive('mode');
      return;
    }
    // The session's surface is fixed at create: only a bubble <-> non-bubble
    // switch needs a restart. normal -> voice KEEPS a session that is already
    // connecting (e.g. one started INSIDE the ANSWER tap, owner bug #3)
    // instead of tearing it down mid-handshake.
    const surfaceChanged = (prev === 'bubble') !== (voiceMode === 'bubble');
    if (live.active) {
      if (surfaceChanged) {
        live.stop();
        setTimeout(startLive, 600);
      }
      return;
    }
    // Already started inside a user gesture moments ago — do not shadow it.
    if (Date.now() - liveRequestedAtRef.current < 1500) return;
    if (liveVoiceEnabled) setTimeout(startLive, 200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceMode, liveVoiceEnabled, live.active]);

  // ── Restore the persisted voice mode on load (owner bug #1/#2) ─────────────
  // 'voice' re-opens the LIVE MIC once the server flag allows it — but only
  // when the browser ALREADY granted the microphone (a page load must never
  // raise a permission prompt). Restoring NEVER opens the chat or speaks a
  // greeting (mute stays pure DND): it only re-enables the mic + listening
  // state, so the owner can talk hands-free right after a reload.
  const liveRestoredRef = useRef(false);
  useEffect(() => {
    if (liveRestoredRef.current || !liveVoiceEnabled) return;
    liveRestoredRef.current = true;
    if (mountedVoiceModeRef.current !== 'voice') return; // load-time state only
    try { if (localStorage.getItem('saik_widget_live_voice') === '0') return; } catch { /* ignore */ }
    void microphoneAlreadyGranted().then((granted) => {
      if (granted && !liveActiveRef.current) live.start();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveVoiceEnabled]);

  // Auto-open the widget whenever a live voice session starts elsewhere (e.g.
  // the user answers a ring) — the live pipeline needs the widget mounted so
  // there is a talking interface (owner report 2026-10-09). We do NOT force the
  // chat panel open in voice/bubble modes (owner directive 2026-10-10): the bubble
  // surface, or the regular dashboard, should show instead.
  useEffect(() => {
    const open = () => { if (!bubbleMode) { suppressRingRef.current = true; setExpanded(true); notifyNewMessage(); } };
    window.addEventListener('saik:start-live', open);
    return () => window.removeEventListener('saik:start-live', open);
  }, [bubbleMode]);

  // Declining (or dismissing) a ring POPS THE AGENT (owner directive 2026-10-10):
  // open the widget so the user can talk/type — no call is started.
  useEffect(() => {
    // Popping the agent (manual open path) is a normal open: an unread incoming
    // message should ring + auto-speak, exactly like tapping the launcher.
    const pop = () => { setExpanded(true); notifyNewMessage(); };
    window.addEventListener('saik:pop-agent', pop);
    return () => window.removeEventListener('saik:pop-agent', pop);
  }, []);

  // AN ANSWER FROM THE OLD RING UI (green bar / full-screen ring) — CONSOLIDATION
  // (owner report 2026-10-13): those surfaces cannot speak. When they hand us the
  // answer, open the chat and SPEAK the agent's reason here (the one call
  // experience). A signed call has precedence; this only covers the ring rows.
  useEffect(() => {
    const onRingAnswer = (ev: Event) => {
      const d = (ev as CustomEvent).detail || {};
      const text = (d.body || '').trim();
      setExpanded(true);
      answeredCallAtRef.current = Date.now(); // an answered call IS the intro
      greetedRef.current = true;
      if (text) autoSpeak(text);
    };
    window.addEventListener('saik:ring-answer', onRingAnswer as EventListener);
    return () => window.removeEventListener('saik:ring-answer', onRingAnswer as EventListener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mid-call flag (owner directive 2026-10-12): while a live voice session is
  // open, RingOverlay + the native bridge must NOT hijack the screen with a new
  // incoming-call popup — the user is already talking. The green bar still shows
  // other ringers.
  useEffect(() => {
    document.documentElement.dataset.saikLiveVoice = live.active ? '1' : '0';
    return () => { document.documentElement.dataset.saikLiveVoice = '0'; };
  }, [live.active]);

  // END BUTTON (owner directive 2026-10-12): ending the live session must stop
  // ALL calling for this session — stop the mic/WS, then terminally dismiss every
  // live ring server-side (no re-rings, no lingering green bar), then re-sync.
  const endLive = useCallback((reason?: string) => {
    live.stop(reason);
    // Ending the live session LEAVES voice mode, persisted (owner bug #2): the
    // mic is off and the next load must not silently re-open it.
    setVoiceMode('normal');
    persistVoiceMode('normal');
    persistLiveVoicePref(false);
    ringsApi.endSession()
      .then(() => { try { window.dispatchEvent(new CustomEvent('saik:rings-refresh')); } catch { /* ignore */ } })
      .catch(() => undefined);
  }, [live]);

  // Poll the selected OpenClaw line's REAL busy state (working/processing).
  useEffect(() => {
    if (agentLine === 'default') { setAgentBusy(false); return; }
    let alive = true;
    const poll = () => {
      chatApi.agentStatus(agentLine)
        .then((s) => { if (alive) setAgentBusy(Boolean(s.busy)); })
        .catch(() => { if (alive) setAgentBusy(false); });
    };
    poll();
    const t = setInterval(poll, 5000);
    return () => { alive = false; clearInterval(t); };
  }, [agentLine]);

  // Drive the avatar state machine from the live session (same events as chat).
  useEffect(() => {
    if (!live.active) return;
    if (live.status === 'thinking') notifyProcessing();
    else if (live.status === 'speaking') notifySpeakingStart();
    else if (live.status === 'listening') notifySpeakingStop();
    else if (live.status === 'error') notifyError();
  }, [live.status, live.active]);

  // Live read-aloud accounting. In live mode the reply audio is server PCM
  // (PcmPlayer). When the device AudioContext stays frozen/suspended (owner
  // report 2026-10-08/09: "not read aloud") the hook flips status back to
  // 'listening' within a tick of every first_audio, so accumulated 'speaking'
  // time stays ~0. We only device-speak the reply when the PCM did NOT audibly
  // play, so a healthy session is never double-voiced.
  const captionRef = useRef('');
  captionRef.current = live.caption;
  // Reliable "did the server actually stream audio this turn?" signal. The
  // accumulated-'speaking'-state proxy below is flaky (the hook flips status
  // back to 'listening' within a tick of first_audio), which made the widget
  // device-speak ON TOP of the server PCM — the owner heard two voices. The
  // server reports first_audio latency on the Status "speaking" transition; we
  // latch that into a per-turn flag (reset on 'thinking'), because
  // live.lastLatencyMs is sticky across turns.
  const pcmPlayedRef = useRef(false);
  const speakingMsRef = useRef(0);
  const speakingStartRef = useRef<number | null>(null);
  useEffect(() => {
    if (live.status === 'speaking') {
      if (speakingStartRef.current == null) speakingStartRef.current = Date.now();
    } else if (speakingStartRef.current != null) {
      speakingMsRef.current += Date.now() - speakingStartRef.current;
      speakingStartRef.current = null;
    }
    if (live.status === 'thinking') { speakingMsRef.current = 0; speakingStartRef.current = null; pcmPlayedRef.current = false; }
    // The server reports its first-audio latency for a turn (null when it
    // streamed no audio). Latch non-null as "the server voiced this turn" —
    // watching lastLatencyMs directly is robust even when status does not
    // change (it is often already 'speaking' on a back-to-back turn).
    if (live.status !== 'thinking' && live.lastLatencyMs != null) pcmPlayedRef.current = true;
  }, [live.status, live.lastLatencyMs]);

  useEffect(() => {
    const receiveVoice = (event: Event) => {
      const detail = (event as CustomEvent<{ target?: string; transcript?: string }>).detail;
      if (detail?.target === 'assistant-widget' && detail.transcript) {
        setInput(detail.transcript);
        setVoiceActive(false);
        // Auto-send after dictation
        setTimeout(() => handleSend(detail.transcript!), 100);
      }
    };
    const receiveVoiceStatus = (event: Event) => {
      const detail = (event as CustomEvent<{ target?: string; phase?: string; message?: string; transcript?: string }>).detail;
      if (detail?.target !== 'assistant-widget') return;
      if (detail.phase === 'listening' || detail.phase === 'requesting') setVoiceActive(true);
      else if (detail.phase === 'error' || detail.phase === 'cancelled' || detail.phase === 'processing') setVoiceActive(false);
      if (detail.transcript) setInput(detail.transcript);
    };
    window.addEventListener('saik:voice-result', receiveVoice);
    window.addEventListener('saik:voice-status', receiveVoiceStatus);
    return () => {
      window.removeEventListener('saik:voice-result', receiveVoice);
      window.removeEventListener('saik:voice-status', receiveVoiceStatus);
    };
  }, []);

  // Proactive on mount (owner directive 2026-10-07): the agent speaks FIRST.
  // Instead of a static "tap me to chat", it fetches a live debrief and shows it
  // in the bubble before the user has said anything.
  const proactiveDebrief = useCallback(async (force = false) => {
    // Throttle the on-load bubble debrief (a refresh should not cost a model call
    // every time); opening the chat always re-debriefs.
    try {
      if (!force) {
        const last = Number(localStorage.getItem('saik_last_greet') || 0);
        if (Date.now() - last < 30 * 60 * 1000) return null;
      }
      const res = await assistantApi.greet(bubbleMode);
      if (res?.message) localStorage.setItem('saik_last_greet', String(Date.now()));
      const text = res?.message;
      if (text) {
        setBubble({ title: effName, body: text });
        if (bubbleTimerRef.current) clearTimeout(bubbleTimerRef.current);
        bubbleTimerRef.current = setTimeout(() => setBubble(null), bubbleMode ? 20000 : 14000);
        return text;
      }
    } catch { /* ignore */ }
    return null;
  }, [assistantName, bubbleMode]);

  useEffect(() => {
    // OWNER RULE (2026-10-13 23:33): automatic communication happens ONLY in
    // speech-bubble mode. With bubble mode OFF (normal / voice) the app opens in
    // OFF mode — no greeting bubble, no auto debrief, no speech. The agent works
    // only when the user taps the launcher and interacts.
    if (!bubbleMode) return;
    // Bubble mode: the agent greets in the bubble on open (handled by the
    // auto-contact effect below, which also speaks when live voice is on).
    // Nothing to seed here — return without a placeholder.
  }, [assistantName, personalityMode, proactiveDebrief, bubbleMode]);

  // Auto-contact on login (owner directive 2026-10-07): when the app opens, the
  // agent opens its chat and debriefs the user FIRST — "the main agent contacts me".
  // Runs once per app session (sessionStorage resets on a new launch/login), NOT on
  // every route change (the widget persists across routes).
  const autoOpenRef = useRef(false);
  useEffect(() => {
    if (autoOpenRef.current) return;
    autoOpenRef.current = true;
    // NOTE (owner directive 2026-10-12 22:46 — supersedes round 2): the app
    // launch NEVER opens the chat or speaks by itself. The debrief shows as a
    // VISUAL launcher-bubble preview only; the ring + read-aloud happen after
    // the user taps the launcher (tap = accept). Round 2's auto-speak-on-launch
    // was the "no accept required" complaint.
    // In speech-bubble mode the main chat must NEVER open itself (owner directive
    // 2026-10-10) — the agent greets in the bubble instead. Ring-first on open is
    // handled by the RingOverlay, which pre-empts this anyway.
    if (bubbleMode) {
      try { sessionStorage.setItem('saik_widget_autogreeted', '1'); } catch { /* ignore */ }
      const t = setTimeout(() => { setBubble(null); void proactiveDebrief(true); }, 1800);
      return () => clearTimeout(t);
    }
    // OWNER RULE (2026-10-13 23:33): with bubble mode OFF the agent must NOT
    // communicate automatically — no greeting, no auto-debrief, no popped bubble,
    // no speech. The app opens in OFF mode; the agent works only when the user
    // taps the launcher. The CALL feature is separate: a genuine open ask still
    // RINGS (ring-first below) — that is the agent calling, not a greeting.
    const t = setTimeout(async () => {
      // Ring-first (owner directive 2026-10-10): if the agent has active rings, the
      // incoming-CALL screen takes precedence and owns the open.
      try {
        const r = await ringsApi.list(false);
        if ((r?.active_count || 0) > 0) return;
      } catch { /* offline — stay quiet */ }
      // The agent calls YOU (owner directive 2026-10-12): a genuine UNANSWERED ask
      // rings on open (the backend applies a cooldown). No greeting is generated.
      try {
        const check = await assistantApi.checkIn();
        if (check?.rang) { window.dispatchEvent(new CustomEvent('saik:rings-refresh')); }
      } catch { /* offline — stay quiet */ }
      // No debrief, no auto-message, no bubble (owner rule 2026-10-13 23:33).
    }, 1800);
    return () => clearTimeout(t);
  }, [proactiveDebrief, bubbleMode]);

  const showBubble = (title: string, body: string, autoDismiss = 8000) => {
    setBubble({ title, body });
    if (bubbleTimerRef.current) clearTimeout(bubbleTimerRef.current);
    if (autoDismiss > 0) {
      bubbleTimerRef.current = setTimeout(() => setBubble(null), autoDismiss);
    }
  };

  const handleSend = async (messageOverride?: string) => {
    const userMsg = (messageOverride ?? input).trim();
    if (!userMsg || loading || !currentWorkspace) return;
    // A user-initiated turn is NOT an incoming message: never ring for it, and
    // silence any in-flight auto-speak (owner directive 2026-10-12).
    cancelAutoSpeak();
    suppressRingRef.current = true;
    // In speech-bubble mode, send the turn flagged as a bubble surface so the
    // agent replies in SHORT sentences (owner directive 2026-10-10).
    const bubbleTurn = bubbleMode && !expanded;
    setInput('');
    // A bubble-mode turn renders ONLY in the bubble (no chat panel churn).
    if (!bubbleTurn) setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);
    notifyNewMessage();
    notifyProcessing();
    try {
      // Route to the SELECTED agent's real conversation (Ricardo/Henry/daily).
      const cid = boundConvRef.current ?? await resolveAgentConversation(agentLine, currentWorkspace.id);
      boundConvRef.current = cid;
      // source:'widget' — only the floating widget may drive screen navigation
      // (it stays alive across route changes); the chat module must not.
      const reply = await chatApi.send(cid, {
        content: userMsg,
        source: bubbleTurn ? 'widget_bubble' : 'widget',
      });
      if (bubbleTurn) {
        // Reply in the bubble; keep it stable until replaced/dismissed.
        showBubble(effName, reply.content, 20000);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: reply.content, id: reply.id }]);
      }
      // The assistant reply to a user turn is the user's own conversation — mark
      // it read so a later re-open never rings for it (owner directive 2026-10-12).
      markRead({ role: 'assistant', content: reply.content, id: reply.id });
      notifySpeakingStart();
      notifySuccess();
      if (soundEnabled) {
        try { new Audio('/sounds/pop.mp3').play().catch(() => {}); } catch {}
      }
      // Auto-stop the talking state after a short delay (mimics speech duration).
      const speechDuration = Math.min(8000, Math.max(2000, reply.content.length * 50));
      setTimeout(() => notifySpeakingStop(), speechDuration);
    } catch {
      notifyError();
      if (bubbleTurn) {
        showBubble(effName, 'Connection error. Please try again.', 8000);
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `[${effName}] Connection error. Please try again.`,
        }]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAction = (text: string) => {
    void handleSend(text);
  };

  // While a live session is open, typed text feeds the live loop instead of the
  // classic chat request — one conversation, one brain.
  const submitInput = () => {
    const text = input.trim();
    if (!text) return;
    if (live.active) {
      live.sendText(text);
      setInput('');
      return;
    }
    void handleSend(text);
  };

  const liveDotColor: Record<string, string> = {
    connecting: 'bg-amber-400',
    listening: 'bg-blue-500',
    thinking: 'bg-amber-400',
    speaking: 'bg-indigo-500',
    error: 'bg-red-500',
  };

  // The listening ring is blue (spec: "blue listening ring"), amber while the
  // model thinks, indigo while it speaks.
  const liveRingClass = (status: string): string => {
    if (status === 'listening') return 'ring-blue-500 animate-pulse';
    if (status === 'thinking' || status === 'connecting') return 'ring-amber-400 animate-pulse';
    if (status === 'speaking') return 'ring-indigo-500';
    if (status === 'error') return 'ring-red-500';
    return 'ring-slate-400';
  };

  const startVoice = () => {
    const bridge = (window as any).webkit?.messageHandlers?.saikVoice;
    if (bridge) bridge.postMessage({ action: 'start', target: 'assistant-widget' });
    else showBubble(effName, 'Voice input is available in the SAIK iPhone app.');
  };

  // Long-press context menu
  const [showContextMenu, setShowContextMenu] = useState(false);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTriggered = useRef(false);
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });

  // Dragging — works on both touch and desktop
  const handlePointerDown = (e: React.PointerEvent) => {
    // Start long-press timer for context menu (touch only)
    if (isTouchLayout) {
      longPressTriggered.current = false;
      setDragStartPos({ x: e.clientX, y: e.clientY });
      longPressTimer.current = setTimeout(() => {
        longPressTriggered.current = true;
        setShowContextMenu(true);
        // Haptic feedback
        if (navigator.vibrate) navigator.vibrate(50);
      }, 500);
    }
    setDragging(true);
    dragRef.current = { offsetX: e.clientX - position.x, offsetY: e.clientY - position.y };
  };
  const handlePointerMove = (e: React.PointerEvent) => {
    if (!dragging) return;
    // Cancel long-press if moved significantly
    if (longPressTimer.current) {
      const dx = Math.abs(e.clientX - dragStartPos.x);
      const dy = Math.abs(e.clientY - dragStartPos.y);
      if (dx > 8 || dy > 8) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
    }
    if (longPressTriggered.current) return; // Don't drag while showing menu
    const next = clampPosition(e.clientX - dragRef.current.offsetX, e.clientY - dragRef.current.offsetY);
    setPosition(next);
  };
  const handlePointerUp = () => {
    setDragging(false);
    persistPosition(position);
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const speakTokenRef = useRef(0);
  const speakWidgetMessage = (text: string) => {
    // Never feed tool-call text / markers to TTS (owner report 2026-09-30).
    const speech = sanitizeForSpeech(text);
    if (!speech) return;
    const bridge = (window as any).webkit?.messageHandlers?.saikSpeech;
    if (bridge) {
      bridge.postMessage({ action: 'speak', target: 'assistant-widget', text: speech, autoListen: false });
      return;
    }
    if (!('speechSynthesis' in window)) return;
    window.speechSynthesis?.cancel();
    // WebKit can drop a speak() issued in the same tick as cancel(); nudge the
    // engine back to a speaking state before the first chunk.
    try { window.speechSynthesis?.resume?.(); } catch { /* ignore */ }
    // iOS/WebKit stalls on long single utterances (owner report 2026-10-12:
    // only the first two words spoke). Speak in short sentence chunks chained
    // via onend, guarded by a token (cancels kill the whole chain) and a
    // watchdog that advances if onend never fires.
    //
    // FULL-REPLAY GUARANTEES (owner bug #5, 2026-10-13: "the agent didn't
    // complete its sentences"):
    //  • a single sentence longer than the ceiling is hard-split on word
    //    boundaries so NO chunk can stall the engine;
    //  • the watchdog is generous (140 ms/char, 4 s floor) — a slow speech rate
    //    must never let it fire while a chunk is still talking (that advanced
    //    the chain mid-sentence and dropped audio);
    //  • a periodic resume() keep-alive drains the queue even when iOS pauses
    //    the engine (interruptions, tab switches) — later chunks are never left
    //    unspoken.
    const speakToken = ++speakTokenRef.current;
    const parts = speech.match(/[^.!?\n]+[.!?]*\s*/g)?.map(s => s.trim()).filter(Boolean) ?? [speech];
    const chunks: string[] = [];
    for (const c of parts) {
      if (chunks.length && chunks[chunks.length - 1].length + c.length < 160) chunks[chunks.length - 1] += ' ' + c;
      else chunks.push(c);
    }
    const sized: string[] = [];
    for (const chunk of chunks) {
      if (chunk.length <= 180) { sized.push(chunk); continue; }
      let piece = '';
      for (const word of chunk.split(' ')) {
        if (piece && piece.length + word.length + 1 > 160) { sized.push(piece); piece = word; }
        else piece = piece ? `${piece} ${word}` : word;
      }
      if (piece) sized.push(piece);
    }
    let idx = 0;
    const keepAlive = window.setInterval(() => {
      try { window.speechSynthesis?.resume?.(); } catch { /* ignore */ }
    }, 5000);
    const speakNext = () => {
      if (idx >= sized.length || speakToken !== speakTokenRef.current) {
        window.clearInterval(keepAlive);
        return;
      }
      const chunk = sized[idx++];
      let done = false;
      let watchdog: number | undefined;
      const advance = () => {
        if (done) return;
        done = true;
        if (watchdog !== undefined) window.clearTimeout(watchdog);
        speakNext();
      };
      const utterance = new SpeechSynthesisUtterance(chunk);
      utterance.lang = navigator.language || 'en-US';
      utterance.onend = advance;
      utterance.onerror = advance;
      window.speechSynthesis?.speak(utterance);
      watchdog = window.setTimeout(advance, Math.max(4000, Math.min(45000, chunk.length * 140)));
    };
    speakNext();
  };

  // ── THE single auto-speak gate (owner directive 2026-10-12, round 2) ────────
  // EVERY automatic (non-manual) utterance passes through here: the unread
  // ring+speak path AND the live PCM-miss fallback. The mute setting silences
  // them ALL, and an active live-voice session owns speech entirely (the widget
  // must never double-voice or pop a speech bubble over a live call). The manual
  // Speak button calls speakWidgetMessage() directly and is NEVER gated.
  const autoSpeak = (text: string) => {
    if (mutedRef.current) return;
    if (liveActiveRef.current) return;
    speakWidgetMessage(text);
  };

  // ── Voice Mode: ring + auto-speak on unread open (owner directive 2026-10-12) ──
  const newestAssistant = (): WidgetMessage | null => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant') return messages[i];
    }
    return null;
  };
  const readKey = () => `saik_widget_last_read_${agentLine}`;
  const messageKey = (m: WidgetMessage) => m.id ?? `c:${m.content}`;
  // Persist what the user has seen so a re-open only rings for NEW messages.
  const markRead = (m?: WidgetMessage | null) => {
    const target = m ?? newestAssistant();
    if (!target) return;
    try { localStorage.setItem(readKey(), messageKey(target)); } catch { /* ignore */ }
  };
  const stopWidgetSpeech = () => {
    speakTokenRef.current++; // kill any in-flight chunk chain
    const bridge = (window as any).webkit?.messageHandlers?.saikSpeech;
    if (bridge) { try { bridge.postMessage({ action: 'stop', target: 'assistant-widget' }); } catch { /* ignore */ } }
    try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
  };
  // User interaction (tap / scroll / type) cancels the auto-speak gracefully.
  const cancelAutoSpeak = () => {
    speakCancelledRef.current = true;
    if (speakTimerRef.current) { clearTimeout(speakTimerRef.current); speakTimerRef.current = null; }
    stopWidgetSpeech();
  };
  const toggleMute = () => {
    setMuted(prev => {
      const next = !prev;
      try { localStorage.setItem('saik_widget_mute', next ? '1' : '0'); } catch { /* ignore */ }
      if (next) cancelAutoSpeak(); // muting immediately silences any auto-speak
      return next;
    });
  };
  // A short, pleasant two-tone chime (~0.8 s) via WebAudio, modest volume.
  const playRing = () => {
    // Always emit the intent signal (observable/testable; harmless live).
    try { window.dispatchEvent(new CustomEvent('saik:widget-ring')); } catch { /* ignore */ }
    try {
      const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const now = ctx.currentTime || 0;
      [[880, 0], [1318.5, 0.28]].forEach(([freq, at]) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.0001, now + at);
        gain.gain.exponentialRampToValueAtTime(0.16, now + at + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + at + 0.26);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now + at);
        osc.stop(now + at + 0.28);
      });
      setTimeout(() => { try { ctx.close(); } catch { /* ignore */ } }, 1000);
    } catch { /* ignore — WebAudio unavailable */ }
  };
  // RING then SPEAK once per open, only for an UNREAD incoming agent message.
  useEffect(() => {
    if (!expanded) {
      ringCheckedRef.current = false;
      suppressRingRef.current = false; // a fresh open is re-evaluated
      return;
    }
    if (ringCheckedRef.current) return;
    if (suppressRingRef.current) { ringCheckedRef.current = true; return; }
    if (muted) return;                 // muted → no ring, no auto-speak
    if (live.active) return;           // live voice takes priority
    const latest = newestAssistant();
    if (!latest) return;               // wait for history to load
    ringCheckedRef.current = true;
    let lastRead: string | null = null;
    try { lastRead = localStorage.getItem(readKey()); } catch { /* ignore */ }
    if (lastRead === messageKey(latest)) return; // already read → stay quiet
    speakCancelledRef.current = false;
    playRing();
    // Reuse the EXISTING speak path (the Speak button) verbatim — no 2nd TTS.
    const text = latest.content;
    if (speakTimerRef.current) clearTimeout(speakTimerRef.current);
    speakTimerRef.current = setTimeout(() => {
      speakTimerRef.current = null;
      if (speakCancelledRef.current) return;
      autoSpeak(text);
    }, 800);
    markRead(latest);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, messages, muted, live.active, agentLine]);
  // Never leave a queued auto-speak running after the widget unmounts.
  useEffect(() => () => {
    if (speakTimerRef.current) { clearTimeout(speakTimerRef.current); speakTimerRef.current = null; }
  }, []);

  // ── THE CALL SCREEN (owner directive 2026-10-12 23:02) ───────────────────────
  // When an agent has an UNREAD message and the user is NOT in an active
  // session (chat closed, no live voice, not muted), the agent CALLS: a
  // full-screen incoming-call screen with a GREEN ANSWER button and DECLINE.
  // Nothing is spoken until the user taps ANSWER — no auto-answer, no
  // "tap anywhere". DECLINE parks the call: the message stays unread and the
  // agent may call again after 5 minutes (if still no active session).
  // A Kimmy debrief that arrives while ANOTHER line is selected: she keeps her
  // own identity — the call screen attributes to HER, and answering switches
  // the widget to her line (owner correction 2026-10-12 23:12).
  const [pendingGreet, setPendingGreet] = useState<{ text: string } | null>(null);
  const injectGreetAfterLoadRef = useRef(false);
  const injectGreetTextRef = useRef<string | null>(null);
  const [callScreen, setCallScreen] = useState<AgentLine | null>(null);
  // ── Agent-SIGNED call requests (owner spec 2026-10-13 01:26) ────────────────
  // The agent is the initiative of the call: it signs a request (reason = what
  // it wants to talk about). The widget polls the pending signed calls and a
  // signed request takes PRECEDENCE over the unread-message heuristic. The call
  // screen renders the signing agent's identity FROM THE REQUEST (display_name +
  // emoji), so arbitrary module agent profiles are supported — the agent_key
  // space is open-ended.
  const [signedCalls, setSignedCalls] = useState<AgentCallRequest[]>([]);
  const [activeSignedCall, setActiveSignedCall] = useState<AgentCallRequest | null>(null);
  const isWidgetLine = (k: string): k is AgentLine => k === 'default' || k === 'ricardo' || k === 'henry';
  const signedCooldownKeyFor = (row: AgentCallRequest) => `saik_widget_call_declined_until_${row.agent_key}`;
  const ringLoopRef = useRef<number | null>(null);
  const [callTick, setCallTick] = useState(0);
  useEffect(() => {
    const iv = window.setInterval(() => setCallTick(t => t + 1), 15000); // cooldown expiry re-check
    return () => window.clearInterval(iv);
  }, []);
  // Poll the agent-SIGNED call requests on mount + every 15 s tick (owner spec
  // 2026-10-13): a request cancelled server-side vanishes on the next tick.
  useEffect(() => {
    let alive = true;
    agentCallsApi.list()
      .then((res) => { if (alive) setSignedCalls((res?.pending || []).filter(c => c.status === 'pending')); })
      .catch(() => undefined); // offline → the heuristic still works
    return () => { alive = false; };
  }, [callTick]);
  const readKeyFor = (line: AgentLine) => `saik_widget_last_read_${line}`;
  const callCooldownKeyFor = (line: AgentLine) => `saik_widget_call_declined_until_${line}`;
  const startCallRing = () => {
    playRing();
    if (ringLoopRef.current) window.clearInterval(ringLoopRef.current);
    ringLoopRef.current = window.setInterval(() => playRing(), 3000);
  };
  const stopCallRing = () => {
    if (ringLoopRef.current) { window.clearInterval(ringLoopRef.current); ringLoopRef.current = null; }
  };
  useEffect(() => {
    if (callScreen) startCallRing();
    else stopCallRing();
    return stopCallRing;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callScreen]);
  useEffect(() => {
    // (0) A SIGNED call request takes PRECEDENCE over the unread-message
    // heuristic (owner spec 2026-10-13): the agent signed the call itself.
    const signed = signedCalls.find(row => {
      try {
        return Date.now() >= Number(localStorage.getItem(signedCooldownKeyFor(row)) || 0);
      } catch { return true; }
    });
    if (signed) {
      if (muted) { setCallScreen(null); setActiveSignedCall(null); return; } // DND
      // ACTIVE-SESSION PROTOCOL applies to signed calls too: park 5 minutes.
      if (expanded || live.active) {
        try {
          const k = signedCooldownKeyFor(signed);
          const parked = Number(localStorage.getItem(k) || 0);
          if (Date.now() >= parked) localStorage.setItem(k, String(Date.now() + 5 * 60 * 1000));
        } catch { /* ignore */ }
        setCallScreen(null); setActiveSignedCall(null); return;
      }
      setActiveSignedCall(signed);
      setCallScreen(isWidgetLine(signed.agent_key) ? signed.agent_key : agentLine);
      return;
    }
    setActiveSignedCall(null);
    // WHO is calling? (1) Kimmy's debrief parked while another line is
    // selected → SHE calls as herself; (2) otherwise an unread message on the
    // selected line → that agent calls (owner correction 2026-10-12 23:12).
    let caller: AgentLine | null = null;
    let callerMsg: WidgetMessage | null = null;
    let parkedGreetCall = false;
    if (agentLine !== 'default' && pendingGreet) {
      try {
        const rk = localStorage.getItem(readKeyFor('default'));
        if (rk !== messageKey({ role: 'assistant', content: pendingGreet.text })) { caller = 'default'; parkedGreetCall = true; }
      } catch { caller = 'default'; parkedGreetCall = true; }
    }
    if (!caller) {
      const latest = newestAssistant();
      if (!latest) { setCallScreen(null); return; }
      let lastRead: string | null = null;
      try { lastRead = localStorage.getItem(readKey()); } catch { /* ignore */ }
      if (lastRead === messageKey(latest)) { setCallScreen(null); return; } // read → no call
      caller = agentLine;
      callerMsg = latest;
    }
    if (muted) { setCallScreen(null); return; } // DND: the unread waits — no call, no clock
    // ACTIVE-SESSION PROTOCOL (owner 2026-10-13 00:13): an agent trying to
    // call while the user is in a session (open chat / live voice) is told to
    // TRY AGAIN IN 5 MINUTES — except the agent whose chat is on screen:
    // its message is already visible, so it is simply marked read.
    if (expanded || live.active) {
      if (caller === agentLine && !parkedGreetCall && callerMsg) {
        markRead(callerMsg); // on screen — no retry clock, no spam
      } else {
        try {
          const k = callCooldownKeyFor(caller);
          const parked = Number(localStorage.getItem(k) || 0);
          if (Date.now() >= parked) localStorage.setItem(k, String(Date.now() + 5 * 60 * 1000));
        } catch { /* ignore */ }
      }
      setCallScreen(null);
      return;
    }
    let until = 0;
    try { until = Number(localStorage.getItem(callCooldownKeyFor(caller)) || 0); } catch { /* ignore */ }
    if (Date.now() < until) { setCallScreen(null); return; } // declined/busy — the agent waits out the 5 min
    setCallScreen(caller);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, messages, muted, live.active, agentLine, callTick, pendingGreet, signedCalls]);
  // PART B (owner spec 2026-10-13 01:26): the owner ANSWERED an agent-SIGNED
  // call. Mark it answered server-side, open the signing agent's chat, seed the
  // turn with the call reason (the backend prompts the agent: "The owner just
  // answered your call. Your call reason: <reason>. Deliver it now, concisely."),
  // and speak the reply through the direct-at-tap speak path (iOS gesture rule).
  const answerSignedCall = async (signed: AgentCallRequest) => {
    try { await agentCallsApi.answer(signed.id); } catch { /* idempotent server-side; the turn still seeds */ }
    setSignedCalls(prev => prev.filter(c => c.id !== signed.id));
    const line: AgentLine = isWidgetLine(signed.agent_key) ? signed.agent_key : agentLine;
    if (line !== agentLine) switchAgent(line);
    const wsId = currentWorkspace?.id;
    if (!wsId) { showBubble(effName, 'Call answer failed — no workspace.', 8000); return; }
    try {
      const cid = await resolveAgentConversation(line, wsId);
      const reply = await chatApi.send(cid, {
        content: `Answered ${signed.display_name}'s call.`,
        source: 'widget',
        answered_call_request_id: signed.id,
      });
      setExpanded(!bubbleMode);
      if (!bubbleMode) setMessages(prev => [...prev, { role: 'assistant', content: reply.content, id: reply.id }]);
      else showBubble(signed.display_name, reply.content, 20000);
      markRead({ role: 'assistant', content: reply.content, id: reply.id });
      autoSpeak(reply.content); // direct speak path, never the open-chat effect
    } catch {
      showBubble(effName, 'Call answer failed. Please try again.', 8000);
    }
  };

  const answerCall = () => {
    stopCallRing();
    // OWNER RULE (2026-10-13 23:33): a call uses whatever configuration the user
    // set for their widget — it must NOT force voice mode. Bubble mode → answer
    // in bubble mode (with the agent's avatar, spoken via the bubble path);
    // normal mode → open the chat normally. Voice mode is left as the user set it.
    // (Previous behaviour force-enabled voice on every answer; that ignored the
    // user's choice, which this directive corrects.)
    const signed = activeSignedCall;
    const caller = callScreen ?? agentLine;
    setCallScreen(null);
    // PART A (owner report 2026-10-13 01:26): AN ANSWERED CALL IS THE
    // INTRODUCTION — whatever the call was (signed request or unread ring),
    // no further auto-generated introduction fires on chat open this visit.
    answeredCallAtRef.current = Date.now();
    greetedRef.current = true;
    if (signed) { setActiveSignedCall(null); void answerSignedCall(signed); return; }
    const greetText = caller === 'default' && pendingGreet ? pendingGreet.text : null;
    if (caller !== agentLine) {
      // The CALLING agent's line takes over (owner correction 2026-10-12
      // 23:12): answering Kimmy while Henry is selected switches the widget
      // and chat to HER — she never talks through Henry's chat.
      switchAgent(caller);
      greetedRef.current = true; // her debrief IS the greet — no duplicate
      ringCheckedRef.current = false; // re-evaluate after her history loads
      if (greetText) { injectGreetAfterLoadRef.current = true; injectGreetTextRef.current = greetText; }
    }
    // Speak AT THE TAP (owner report 2026-10-13 00:13: the SECOND answered
    // call stayed silent when the read-aloud waited on the history load +
    // 800 ms chime timer — iOS drops speech detached from the gesture).
    // Read-marked up front so the open-chat effect never double-speaks.
    if (greetText) {
      setExpanded(!bubbleMode);
      autoSpeak(greetText);
      try { localStorage.setItem(readKeyFor('default'), messageKey({ role: 'assistant', content: greetText })); } catch { /* ignore */ }
      setPendingGreet(null);
      return;
    }
    if (bubbleMode) {
      // Bubble mode never opens the main chat (owner 2026-10-10): answering
      // speaks the unread message in place.
      const latest = newestAssistant();
      if (latest) { markRead(latest); autoSpeak(latest.content); }
      return;
    }
    const latest = newestAssistant();
    setExpanded(true);
    if (latest && caller === agentLine) { markRead(latest); autoSpeak(latest.content); }
  };
  const declineCall = () => {
    stopCallRing();
    const signed = activeSignedCall;
    const caller = callScreen ?? agentLine;
    setCallScreen(null);
    setActiveSignedCall(null);
    // Signed call → cooldown keyed by the signing agent_key (open-ended profile
    // space); heuristic ring → the 3 hardcoded lines. Same 5-minute park.
    try {
      const key = signed ? signedCooldownKeyFor(signed) : callCooldownKeyFor(caller);
      localStorage.setItem(key, String(Date.now() + 5 * 60 * 1000));
    } catch { /* ignore */ }
  };

  // A live turn finished — the server persisted the marked reply and dispatched
  // 'saik:voice-turn' on turn_end. Surface the agent's designated reply in the
  // widget thread (the messenger) and read it aloud when the live PCM did not
  // play. Raw reasoning/captions are never shown here.
  useEffect(() => {
    const handler = () => {
      const reply = (captionRef.current || '').trim();
      // Single-voice guarantee: if the server streamed audio this turn, the PCM
      // is the ONE voice. Never device-speak a reply the server already voiced;
      // only fall back to device TTS when the server did NOT play this turn.
      const pcmAudible = pcmPlayedRef.current;
      speakingMsRef.current = 0;
      speakingStartRef.current = null;
      // Gated fallback: only device-speak when the server did NOT voice the turn
      // AND auto-speak is allowed (unmuted, no live session). Never an ungated
      // speak — that was the "speech bubble popping up with both turned off" bug.
      if (reply && !pcmAudible) autoSpeak(reply);
      // Speech-bubble mode (owner 2026-10-10): the reply lands in the bubble
      // (there is no open chat panel), so write the thread into messages but
      // render the reply bubble right now.
      if (bubbleMode && reply) showBubble(effName, reply, 20000);
      const wsId = currentWorkspace?.id;
      if (!wsId) return;
      const bound = boundConvRef.current;
      (bound ? Promise.resolve(bound) : resolveAgentConversation(agentLine, wsId))
        .then((id) => chatApi.messages(id))
        .then((rows) => {
          const mapped = rows
            .filter((m) => m.role !== 'system' && (m.content || '').trim())
            .map((m): WidgetMessage => ({ role: m.role === 'user' ? 'user' : 'assistant', content: m.content, id: m.id }));
          if (mapped.length) setMessages(mapped);
        })
        .catch(() => undefined);
    };
    window.addEventListener('saik:voice-turn', handler);
    return () => window.removeEventListener('saik:voice-turn', handler);
  }, [currentWorkspace?.id, bubbleMode]);

  const disableAssistant = async () => {
    try {
      await assistantApi.updatePrefs({ enabled: false });
      setShowContextMenu(false);
      // Trigger a page refresh of the provider context
      window.location.reload();
    } catch {
      setShowContextMenu(false);
    }
  };

  return (
    <>
      {/* Floating Avatar Bubble — hidden while the chat panel is open so the
          panel can never overlap the avatar (owner-reported defect, 2026-09-20:
          "the Kimmy popup overlaps the assistant avatar"). The panel header
          carries the animated avatar thumbnail instead. */}
      {/* ── INCOMING CALL SCREEN (owner directive 2026-10-12 23:02) ──────────
          The agent calls; the user MUST tap the green ANSWER button or DECLINE.
          No auto-answer, no auto-dismiss — an explicit choice, always. */}
      {callScreen && (
        <div
          className="fixed inset-0 z-[95] flex flex-col items-center justify-center bg-black/80 backdrop-blur-sm px-6"
          data-agent-id="widget.call.screen"
        >
          <div className="flex flex-col items-center gap-3">
            <div className="flex h-24 w-24 select-none items-center justify-center rounded-full bg-gradient-to-b from-neutral-700 to-neutral-900 text-5xl shadow-lg">
              {activeSignedCall?.emoji ?? AGENTS[callScreen]?.emoji ?? '🤖'}
            </div>
            <div className="mt-2 text-xl font-semibold text-white">
              {(activeSignedCall?.display_name ?? (callScreen ? AGENTS[callScreen].name : effName))} is calling
            </div>
            {/* Signed call: the agent's OWN reason rides the screen (owner spec
                2026-10-13 — identity comes FROM THE REQUEST, so arbitrary module
                agent profiles render without frontend hardcoding). */}
            <div className="text-sm text-white/60">
              {activeSignedCall?.reason?.trim()
                ? activeSignedCall.reason
                : 'Unread message — answer to hear it'}
            </div>
          </div>
          <div className="mt-12 flex items-center gap-16">
            <button
              type="button"
              onClick={declineCall}
              data-agent-id="widget.call.decline"
              aria-label="Decline call"
              className="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-full bg-red-500 text-white shadow-lg transition active:scale-95"
            >
              <span className="text-2xl leading-none">✕</span>
              <span className="text-[10px] font-medium">Decline</span>
            </button>
            <button
              type="button"
              onClick={answerCall}
              data-agent-id="widget.call.answer"
              aria-label="Answer call"
              className="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-full bg-green-500 text-white shadow-lg transition active:scale-95"
            >
              <span className="text-2xl leading-none">📞</span>
              <span className="text-[10px] font-medium">Answer</span>
            </button>
          </div>
        </div>
      )}
      <div
        hidden={expanded}
        data-agent-id="widget.launcher"
        aria-label={`Open chat with ${effName}`}
        role="button"
        tabIndex={expanded ? -1 : 0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded(true);
            notifyNewMessage();
          }
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onClick={() => {
          if (!dragging && !longPressTriggered.current) {
            setExpanded(value => {
              if (value) {
                setMessages([]);
                greetedRef.current = false; // re-greet next time it opens
                notifyUserActivity(); // chat closed > wake/reset
              } else {
                notifyNewMessage(); // chat opened > alert
                // Proactive: the agent debriefs first (owner directive 2026-10-07).
                // ONLY the default (main assistant) line debriefs — Henry's and
                // Ricardo's chats show their own history, never Kimmy's text
                // (owner correction 2026-10-12 23:12).
                if (!greetedRef.current && !answeredCallSuppressesGreet() && agentLine === 'default') {
                  greetedRef.current = true;
                  setLoading(true);
                  proactiveDebrief(true).then(text => {
                    if (text) setMessages(prev => [...prev, { role: 'assistant', content: text }]);
                  }).finally(() => setLoading(false));
                } else {
                  greetedRef.current = true;
                }
              }
              return !value;
            });
          }
          longPressTriggered.current = false;
        }}
        style={isTouchLayout
          ? { left: position.x, top: position.y, touchAction: 'none' }
          : { left: position.x, top: position.y }}
        className="fixed z-50 cursor-pointer select-none touch-manipulation"
      >
        {/* Speech Bubble */}
        {bubble && !expanded && (
          <div className={`absolute bottom-full right-0 mb-2 ${bubbleMode ? 'w-[min(22rem,calc(100vw-2rem))]' : 'w-64'} rounded-xl bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-700 p-3 animate-fadeIn`} data-agent-id="widget.bubble">
            {bubble.title && (
              <div className="text-xs font-bold text-indigo-600 dark:text-indigo-400 mb-0.5">
                {bubble.title}
              </div>
            )}
            <div className={`text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap ${bubbleMode ? 'max-h-56 overflow-y-auto leading-snug' : ''}`}>{bubble.body}</div>
            <button
              onClick={(e) => { e.stopPropagation(); setBubble(null); }}
              className="absolute top-1 right-2 text-gray-400 hover:text-gray-600 text-xs"
            >✕</button>
          </div>
        )}

        {/* Avatar — state-machine-driven sprite animator with static fallback */}
        {avatarImagePath && isFullBodyAvatar ? (
          <div
            className="relative cursor-pointer select-none"
            style={{
              width: isTouchLayout ? '80px' : '90px',
              height: isTouchLayout ? '120px' : '135px',
            }}
          >
            <Suspense fallback={
              <img
                src={avatarImagePath}
                alt={effName}
                className="w-full h-full object-contain drop-shadow-lg"
                style={{ filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.3))' }}
              />
            }>
              <AvatarAnimatorLazy
                characterId={effAvatar}
                state={avatarState}
                size={{
                  width: isTouchLayout ? 80 : 90,
                  height: isTouchLayout ? 120 : 135,
                }}
                animationEnabled={animationEnabled}
                onError={() => {}}
              />
            </Suspense>
            {/* Pulse indicator at feet */}
            <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-brand-500 animate-pulse" />
          </div>
        ) : (
          <div className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg ring-2 ring-white dark:ring-gray-800 hover:scale-110 transition-transform overflow-hidden ${avatarImagePath ? '' : `bg-gradient-to-br ${gradient}`}`}>
            {avatarImagePath ? (
              <img src={avatarImagePath} alt={effName} className="w-full h-full object-cover" />
            ) : (
              <span className="text-white text-xl font-bold">
                {effName.charAt(0).toUpperCase()}
              </span>
            )}
            {/* Pulse Ring */}
            <div className={`absolute inset-0 rounded-full ${avatarImagePath ? 'bg-brand-500' : `bg-gradient-to-br ${gradient}`} animate-ping opacity-20`} />
          </div>
        )}
      </div>

      {/* Long-press context menu */}
      {showContextMenu && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setShowContextMenu(false)} />
          <div
            style={isTouchLayout
              ? { left: Math.min(position.x, window.innerWidth - 180), top: Math.max(10, position.y - 100) }
              : { left: position.x, top: Math.max(10, position.y - 100) }}
            className="fixed z-[61] w-44 rounded-xl bg-white dark:bg-gray-800 shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden"
          >
            <button
              onClick={disableAssistant}
              className="flex w-full items-center gap-2 px-4 py-3 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              <span>Disable Assistant</span>
            </button>
            <button
              onClick={() => { setShowContextMenu(false); setExpanded(true); notifyNewMessage(); }}
              className="flex w-full items-center gap-2 px-4 py-3 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors border-t border-gray-200 dark:border-gray-700"
            >
              <span>Open Chat</span>
            </button>
            {/* Voice interaction mode (owner directive 2026-10-10): voice and speech
                bubble are parallel modes — only one runs at once (both use the mic). */}
            <div className="border-t border-gray-200 dark:border-gray-700 px-3 py-2" data-agent-id="widget.voice-mode">
              <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">Voice mode</div>
              {([
                { id: 'normal', label: 'Off (tap to talk)', hint: '' },
                { id: 'voice', label: 'Voice mode', hint: 'Hands-free — speaks and listens.' },
                { id: 'bubble', label: 'Speech bubble', hint: 'Listens; short replies in the bubble.' },
              ] as const).map((m) => (
                <button
                  key={m.id}
                  data-agent-id={`widget.voice-mode.${m.id}`}
                  onClick={() => changeVoiceMode(m.id)}
                  className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm transition-colors ${
                    voiceMode === m.id
                      ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 font-medium'
                      : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                  }`}
                >
                  {m.id === 'bubble' ? <MessageSquare size={14} /> : m.id === 'voice' ? <Radio size={14} /> : <Mic size={14} />}
                  <span>{m.label}</span>
                  {voiceMode === m.id && (
                    <span className="ml-auto flex items-center gap-1">
                      {live.active && (voiceMode !== 'normal') && <span className="h-2 w-2 rounded-full bg-indigo-500 animate-pulse" />}
                      <span className="text-xs">✓</span>
                    </span>
                  )}
                </button>
              ))}
              {voiceMode !== 'normal' && (
                <p className="px-2 pt-1 text-[10px] leading-snug text-slate-400">
                  {voiceMode === 'bubble'
                    ? 'Listening — replies appear in a short bubble. Voice mode is off while this is on.'
                    : 'Listening — hands-free voice mode.'}
                </p>
              )}
            </div>
            {/* Agent selector — talk to the real agent (Ricardo / Henry) */}
            <div className="border-t border-gray-200 dark:border-gray-700 px-3 py-2">
              <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">Talk to</div>
              {(['default', 'ricardo', 'henry'] as AgentLine[]).map((line) => (
                <button
                  key={line}
                  data-agent-id={`widget.agent.${line}`}
                  onClick={() => { switchAgent(line); setShowContextMenu(false); setExpanded(true); notifyNewMessage(); }}
                  className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm transition-colors ${
                    agentLine === line
                      ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 font-medium'
                      : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                  }`}
                >
                  <span>{AGENTS[line].emoji}</span>
                  <span>{AGENTS[line].label}</span>
                  {agentLine === line && <span className="ml-auto text-xs">✓</span>}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Expanded Chat Panel */}
      {expanded && (
        <>
          {/* Modal scrim — dim the background behind the sheet (owner HIG 2026-10-12) */}
          <div
            data-agent-id="widget.scrim"
            aria-hidden="true"
            onPointerDown={cancelAutoSpeak}
            onClick={() => { setExpanded(false); setMessages([]); setInput(''); notifyUserActivity(); }}
            className="fixed inset-0 z-40 bg-black/40 dark:bg-black/60 backdrop-blur-[1px] animate-fadeIn"
          />
          <div
            data-agent-id="widget.panel"
            // NOTE (owner bug #5, 2026-10-13): taps/typing INSIDE the panel no
            // longer cancel in-flight speech — that killed the reply mid-sentence
            // the moment the owner touched the transcript. Speech still stops on
            // explicit intents: scrim tap, close, or a new turn (handleSend).
            style={isTouchLayout ? {
              left: 16,
              right: 16,
              bottom: 'max(90px, calc(env(safe-area-inset-bottom) + 80px))',
              height: 'min(62vh, 520px)',
            } : {
              left: Math.max(16, Math.min(position.x, window.innerWidth - 356)),
              top: Math.max(16, Math.min(position.y - 420, window.innerHeight - 456)),
            }}
            className="fixed z-50 w-auto sm:w-[340px] max-w-[calc(100vw-32px)] h-[440px] rounded-2xl bg-white dark:bg-gray-900 shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden animate-slideUp"
          >
          {/* Header */}
          <div className="flex items-center justify-between gap-2 px-4 py-3 min-h-[64px] border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3 min-w-0">
              <div className={`flex items-center justify-center overflow-hidden ${avatarImagePath && isFullBodyAvatar ? 'w-8 h-12' : 'w-8 h-8 rounded-full'} ${!avatarImagePath ? `bg-gradient-to-br ${gradient}` : ''}`}>
                {avatarImagePath && isFullBodyAvatar ? (
                  <Suspense fallback={
                    <img
                      src={avatarImagePath}
                      alt={effName}
                      className="w-full h-full object-contain"
                    />
                  }>
                    <AvatarAnimatorLazy
                      characterId={effAvatar}
                      state={avatarState}
                      size={{ width: 32, height: 48 }}
                      animationEnabled={animationEnabled}
                      onError={() => {}}
                    />
                  </Suspense>
                ) : avatarImagePath ? (
                  <img src={avatarImagePath} alt={effName} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-white text-sm font-bold">{effName.charAt(0).toUpperCase()}</span>
                )}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">{effName}</span>
                  <span
                    data-agent-id="widget.status"
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${agentBusy ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'}`}
                  >
                    <span className={`h-1.5 w-1.5 rounded-full ${agentBusy ? 'bg-amber-500 animate-pulse' : 'bg-emerald-500'}`} />
                    {agentBusy ? 'Working' : 'Online'}
                  </span>
                </div>
                <div className="text-[11px] text-gray-500 capitalize leading-tight truncate">
                  {agentLine === 'default' ? `${personalityMode} mode` : `${agent.emoji} ${agent.label}`}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={toggleMute}
              data-agent-id="widget.mute"
              aria-label={muted ? 'Unmute ring and voice' : 'Mute ring and voice'}
              aria-pressed={muted}
              className="flex items-center justify-center shrink-0 w-11 h-11 min-w-[44px] min-h-[44px] rounded-full text-gray-500 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 active:scale-95 transition"
            >
              {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>
            <button
              type="button"
              onClick={() => { cancelAutoSpeak(); setExpanded(false); setMessages([]); setInput(''); notifyUserActivity(); }}
              data-agent-id="widget.close"
              aria-label="Close chat"
              className="flex items-center justify-center shrink-0 w-11 h-11 min-w-[44px] min-h-[44px] rounded-full text-gray-500 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 active:scale-95 transition text-lg leading-none"
            >✕</button>
          </div>

          {/* Messages */}
          {/* Messages — scrolling to read must NEVER truncate the spoken reply
              (owner bug #5, 2026-10-13: onScroll used to cancel auto-speak). */}
          <div className="flex-1 overflow-y-auto px-3 py-2.5">
            <div className="flex flex-col gap-2 justify-end min-h-full">
            {messages.length === 0 && (
              <div className="text-center text-sm text-gray-400 py-8">
                Chat with {effName}! Ask me anything.
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={i} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                <div className={`max-w-[78%] rounded-2xl px-3 py-3 text-sm leading-snug ${
                  msg.role === 'user'
                    ? 'bg-indigo-600 text-white rounded-br-sm'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white rounded-bl-sm'
                }`}>
                  {msg.content}
                </div>
                {msg.role === 'assistant' && (
                  <div className="mt-1 flex items-center gap-1.5">
                    <button
                      onClick={() => speakWidgetMessage(msg.content)}
                      className="inline-flex items-center gap-1.5 min-h-[36px] px-2 rounded-lg text-[13px] text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition"
                      aria-label="Play message aloud"
                    >
                      <Volume2 size={14} /> Speak
                    </button>
                    <button
                      onClick={() => { navigator.clipboard?.writeText(msg.content).catch(() => {}); setCopiedIdx(i); setTimeout(() => setCopiedIdx(-1), 2000); }}
                      className="inline-flex items-center gap-1.5 min-h-[36px] px-2 rounded-lg text-[13px] text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition"
                      aria-label="Copy message"
                    >
                      {copiedIdx === i ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
                    </button>
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 dark:bg-gray-800 rounded-full px-3.5 py-2.5 text-gray-500">
                  <span className="inline-flex items-center gap-1">
                    <span className="w-1 h-1 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1 h-1 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-1 h-1 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Quick Actions — single row, horizontally scrollable so pills are
              never clipped at the bottom edge (owner HIG 2026-10-12). */}
          <div className="flex items-center gap-2 px-3 pb-2 overflow-x-auto whitespace-nowrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {["What's next?", "Review tasks", "Suggest something"].map(q => (
              <button
                key={q}
                onClick={() => handleQuickAction(q)}
                disabled={loading}
                className="shrink-0 inline-flex items-center min-h-[36px] px-3 rounded-full bg-indigo-50 dark:bg-indigo-900/30 text-[13px] text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-800/40 disabled:opacity-50 transition"
              >
                {q}
              </button>
            ))}
          </div>

          {/* Live voice (P1): caption + status while a session is open */}
          {live.active && (
            <div
              data-voice-caption={live.status}
              className="px-3 py-2 border-t border-indigo-100 dark:border-indigo-900/40 bg-indigo-50/60 dark:bg-indigo-900/20"
            >
              <div className="flex items-center gap-2">
                <span
                  className={`h-2 w-2 rounded-full ${liveDotColor[live.status] ?? 'bg-slate-400'} ${
                    live.status === 'listening' ? 'animate-pulse' : ''
                  }`}
                />
                <span className="text-[10px] uppercase tracking-wide text-indigo-700 dark:text-indigo-300">
                  {live.status}
                </span>
                {live.lastLatencyMs != null && (
                  <span className="text-[10px] text-slate-500">{live.lastLatencyMs} ms to first audio</span>
                )}
                <button
                  type="button"
                  title="End live voice"
                  aria-label="End live voice"
                  onClick={() => endLive()}
                  className="ml-auto text-[10px] text-red-500 hover:underline"
                >
                  End
                </button>
              </div>
              {/* Voice-only panel (owner spec + task 255bfa40 / fd3fb32a): NO transcript
                  and NO reasoning/caption box. The model's thinking stays in the
                  background; only the designated (marked) reply is spoken and lands in
                  the chat thread. Here we keep just the status line above (+ any error). */}
              {live.error && <div className="mt-0.5 text-[10px] text-red-500">{live.error}</div>}
            </div>
          )}

          {/* Input — a single rounded composer pill; send sits inside it.
              Safe-area aware so nothing touches the home indicator (HIG). */}
          <div
            className="px-3 pt-2 border-t border-gray-200 dark:border-gray-700"
            style={{ paddingBottom: 'max(8px, env(safe-area-inset-bottom))' }}
          >
            <div className="flex items-center gap-2 rounded-full bg-gray-100 dark:bg-gray-800 pl-4 pr-1.5 py-1">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitInput(); } }}
                placeholder={`Message ${effName}…`}
                data-agent-id="widget.composer"
                className="flex-1 min-w-0 bg-transparent text-gray-900 dark:text-white text-sm py-2 focus:outline-none placeholder-gray-400"
              />
              <button
                type="button"
                onClick={startVoice}
                aria-label={voiceActive ? 'Voice input active' : 'Speak message'}
                data-agent-id="widget.mic"
                className="w-10 h-10 min-w-[40px] min-h-[40px] rounded-full text-indigo-600 flex items-center justify-center hover:bg-indigo-50 dark:hover:bg-indigo-900/30 active:scale-95 transition flex-shrink-0"
              >
                {voiceActive ? <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-red-500" /> : <Mic size={18} />}
              </button>
              {liveVoiceEnabled && !bubbleMode && (
                <button
                  type="button"
                  onClick={() => {
                    // NO DEAD-PATH MIC (owner bug #4, 2026-10-13): with voice
                    // mode OFF this tap ENABLES voice mode (persisted) and
                    // starts the live mic — it can never listen/transcribe into
                    // a silent void. The dictation mic (widget.mic) stays a
                    // separate, clearly-labeled control.
                    if (voiceMode !== 'voice') { enableVoiceMode(); return; }
                    if (live.active) endLive();
                    else live.toggle();
                  }}
                  aria-label={voiceMode !== 'voice' ? 'Turn on voice mode' : live.active ? 'End live voice session' : 'Start live voice session'}
                  data-agent-id="widget.hands-free"
                  className={`relative w-10 h-10 min-w-[40px] min-h-[40px] rounded-full flex items-center justify-center active:scale-95 transition flex-shrink-0 ${
                    voiceMode === 'voice'
                      ? 'bg-indigo-600 text-white'
                      : 'text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30'
                  }`}
                >
                  <Radio size={18} />
                  {live.active && (
                    <span
                      className={`absolute -inset-0.5 rounded-full ring-2 ${liveRingClass(live.status)}`}
                      data-voice-ring={live.status}
                    />
                  )}
                </button>
              )}
              <button
                onClick={submitInput}
                disabled={loading || !input.trim()}
                data-agent-id="widget.send"
                aria-label="Send message"
                className="w-10 h-10 min-w-[40px] min-h-[40px] rounded-full bg-indigo-600 text-white flex items-center justify-center hover:bg-indigo-700 active:scale-95 disabled:opacity-50 transition flex-shrink-0"
              >
                ↑
              </button>
            </div>
          </div>
          </div>
        </>
      )}
    </>
  );
}
